import {
  AddressChangeForOwnerDto,
  AutomaticPremiumLoanProvisionDto,
  CorrespondenceLetterSummaryDto,
  FuturePurchaseOptionsDto,
  InitialPremiumNotReceivedDto,
  LoanPaidInFullDto,
  PagedResultOfCorrespondenceLetterSummaryDto,
  PolicyDto,
  PolicyLapseADto,
  PolicyLapseDto,
  PolicyPreLapseDto,
  PremiumChangeDueToExpiryOfSupplementBenefitDto,
  PremiumNoticeDto,
  PremiumPaidUpDto,
  RenewableTermPolicyPremiumChangeDto,
  RequestToCancelDto,
  ReturnedPaymentDto,
  UniversalLifeGraceLetterAFDto,
  UniversalLifeGraceLetterDto,
  WithholdingFromLoanOrPartialWithdrawalsDto,
} from 'Agent/Policies/api/api';
import unionize, {ofType, UnionOf} from 'unionize';

export type Policy = PolicyDto;
export type CorrespondenceLetterSummary = CorrespondenceLetterSummaryDto;
export type CorrespondenceLetterPage = PagedResultOfCorrespondenceLetterSummaryDto;

const LetterTypeMapping = {
  PremiumNotice: ['PA-74AF', 'PA-74', 'NYPA-74AF'],
  RequestToCancel: ['PA-59', 'NYPA-59'],
  Section125CancellationConfirmation: ['PA-57'],
  InitialPremiumNotReceived: ['PA-41', 'NYPA-41'],
  ReturnedPayment: ['PA-22AF-EFT'],
  PolicyPreLapse: ['NYPA-103', 'PA-103'],
  PolicyLapseA: ['01PHS03-55A', '02PHS03-55A'],
  PolicyLapse: ['02PHS03-55', '01PHS03-55'],
  AddressChangeForOwner: ['PA-92', 'PA-92AF', 'NYPA-92AF'],
  FuturePurchaseOptions: ['NYPHS-133', 'PHS03-133'],
  UniversalLifeGraceLetter: ['PHS03-54', 'NYPHS-54'],
  UniversalLifeGraceLetterAF: ['PHS03-54AF'],
  WithholdingFromLoanOrPartialWithdrawals: ['PHS03-57'],
  AutomaticPremiumLoanProvision: ['PHS03-59'],
  PremiumPaidUp: ['PHS03-62'],
  RenewableTermPolicyPremiumChange: ['PHS03-63', 'NYPHS-63'],
  PremiumChangeDueToExpiryOfSupplementBenefit: ['PHS03-86', 'NYPHS-86'],
  LoanPaidInFull: ['PHS03-87'],
};

export const CorrespondenceLetter = unionize({
  AddressChangeForOwner: ofType<AddressChangeForOwnerDto>(),
  AutomaticPremiumLoanProvision: ofType<AutomaticPremiumLoanProvisionDto>(),
  FuturePurchaseOptions: ofType<FuturePurchaseOptionsDto>(),
  InitialPremiumNotReceived: ofType<InitialPremiumNotReceivedDto>(),
  LoanPaidInFull: ofType<LoanPaidInFullDto>(),
  PolicyLapse: ofType<PolicyLapseDto>(),
  PolicyLapseA: ofType<PolicyLapseADto>(),
  PolicyPreLapse: ofType<PolicyPreLapseDto>(),
  PremiumChangeDueToExpiryOfSupplementBenefit: ofType<PremiumChangeDueToExpiryOfSupplementBenefitDto>(),
  PremiumNotice: ofType<PremiumNoticeDto>(),
  PremiumPaidUp: ofType<PremiumPaidUpDto>(),
  RenewableTermPolicyPremiumChange: ofType<RenewableTermPolicyPremiumChangeDto>(),
  RequestToCancel: ofType<RequestToCancelDto>(),
  ReturnedPayment: ofType<ReturnedPaymentDto>(),
  UniversalLifeGraceLetter: ofType<UniversalLifeGraceLetterDto>(),
  UniversalLifeGraceLetterAF: ofType<UniversalLifeGraceLetterAFDto>(),
  WithholdingFromLoanOrPartialWithdrawals: ofType<WithholdingFromLoanOrPartialWithdrawalsDto>(),
});

export type CorrespondenceLetter = UnionOf<typeof CorrespondenceLetter>;

export const mapCorrespondenceLetter = (detail: CorrespondenceDetail): CorrespondenceLetter | null => {
  const letterNumber = detail.number!;

  return LetterTypeMapping.PremiumNotice.includes(letterNumber)
    ? CorrespondenceLetter.PremiumNotice(detail)
    : LetterTypeMapping.AddressChangeForOwner.includes(letterNumber)
    ? CorrespondenceLetter.AddressChangeForOwner(detail)
    : LetterTypeMapping.AutomaticPremiumLoanProvision.includes(letterNumber)
    ? CorrespondenceLetter.AutomaticPremiumLoanProvision(detail)
    : LetterTypeMapping.FuturePurchaseOptions.includes(letterNumber)
    ? CorrespondenceLetter.FuturePurchaseOptions(detail)
    : LetterTypeMapping.InitialPremiumNotReceived.includes(letterNumber)
    ? CorrespondenceLetter.InitialPremiumNotReceived(detail as InitialPremiumNotReceivedDto)
    : LetterTypeMapping.LoanPaidInFull.includes(letterNumber)
    ? CorrespondenceLetter.LoanPaidInFull(detail)
    : LetterTypeMapping.PolicyLapseA.includes(letterNumber)
    ? CorrespondenceLetter.PolicyLapseA(detail as PolicyLapseADto)
    : LetterTypeMapping.PolicyLapse.includes(letterNumber)
    ? CorrespondenceLetter.PolicyLapse(detail as PolicyLapseDto)
    : LetterTypeMapping.PolicyPreLapse.includes(letterNumber)
    ? CorrespondenceLetter.PolicyPreLapse(detail)
    : LetterTypeMapping.PremiumChangeDueToExpiryOfSupplementBenefit.includes(letterNumber)
    ? CorrespondenceLetter.PremiumChangeDueToExpiryOfSupplementBenefit(detail)
    : LetterTypeMapping.PremiumNotice.includes(letterNumber)
    ? CorrespondenceLetter.PremiumNotice(detail)
    : LetterTypeMapping.PremiumPaidUp.includes(letterNumber)
    ? CorrespondenceLetter.PremiumPaidUp(detail)
    : LetterTypeMapping.RenewableTermPolicyPremiumChange.includes(letterNumber)
    ? CorrespondenceLetter.RenewableTermPolicyPremiumChange(detail)
    : LetterTypeMapping.RequestToCancel.includes(letterNumber)
    ? CorrespondenceLetter.RequestToCancel(detail)
    : LetterTypeMapping.ReturnedPayment.includes(letterNumber)
    ? CorrespondenceLetter.ReturnedPayment(detail)
    : LetterTypeMapping.UniversalLifeGraceLetterAF.includes(letterNumber)
    ? CorrespondenceLetter.UniversalLifeGraceLetterAF(detail)
    : LetterTypeMapping.UniversalLifeGraceLetter.includes(letterNumber)
    ? CorrespondenceLetter.UniversalLifeGraceLetter(detail)
    : LetterTypeMapping.WithholdingFromLoanOrPartialWithdrawals.includes(letterNumber)
    ? CorrespondenceLetter.WithholdingFromLoanOrPartialWithdrawals(detail)
    : null;
};

export type CorrespondenceDetail =
  | AddressChangeForOwnerDto
  | AutomaticPremiumLoanProvisionDto
  | FuturePurchaseOptionsDto
  | InitialPremiumNotReceivedDto
  | LoanPaidInFullDto
  | PolicyLapseADto
  | PolicyLapseDto
  | PolicyPreLapseDto
  | PremiumChangeDueToExpiryOfSupplementBenefitDto
  | PremiumNoticeDto
  | PremiumPaidUpDto
  | RenewableTermPolicyPremiumChangeDto
  | RequestToCancelDto
  | ReturnedPaymentDto
  | UniversalLifeGraceLetterDto
  | WithholdingFromLoanOrPartialWithdrawalsDto;

export type AddressChangeForOwner = AddressChangeForOwnerDto;
export type AutomaticPremiumLoanProvision = AutomaticPremiumLoanProvisionDto;
export type FuturePurchaseOptions = FuturePurchaseOptionsDto;
export type InitialPremiumNotReceived = InitialPremiumNotReceivedDto;
export type LoanPaidInFull = LoanPaidInFullDto;
export type PolicyLapseA = PolicyLapseADto;
export type PolicyLapse = PolicyLapseDto;
export type PolicyPreLapse = PolicyPreLapseDto;
export type PremiumChangeDueToExpiryOfSupplementBenefit = PremiumChangeDueToExpiryOfSupplementBenefitDto;
export type PremiumNotice = PremiumNoticeDto;
export type PremiumPaidUp = PremiumPaidUpDto;
export type RenewableTermPolicyPremiumChange = RenewableTermPolicyPremiumChangeDto;
export type RequestToCancel = RequestToCancelDto;
export type ReturnedPayment = ReturnedPaymentDto;
export type UniversalLifeGraceLetter = UniversalLifeGraceLetterDto;
export type UniversalLifeGraceAFLetter = UniversalLifeGraceLetterAFDto;
export type WithholdingFromLoanOrPartialWithdrawals = WithholdingFromLoanOrPartialWithdrawalsDto;
